"use client";

import * as React from "react"
import { nanoid } from 'nanoid';
import { cn } from "@/lib/utils"
import { useLivingCardInteraction } from "@/hooks/useLivingCardInteraction";
import { useLivingCards } from "@/context/livingCards";

function Card({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

// Helper to merge refs
function useMergedRef<T extends Element>(
  ...refs: Array<React.MutableRefObject<T> | React.LegacyRef<T> | null | undefined>
): React.RefCallback<T> {
  return React.useCallback(
    (value: T) => {
      refs.forEach((ref) => {
        if (typeof ref === "function") {
          ref(value);
        } else if (ref != null) {
          (ref as React.MutableRefObject<T | null>).current = value;
        }
      });
    },
    [refs]
  );
}

// Props for CardAnimated
interface CardAnimatedProps extends React.ComponentProps<"div"> {
  id?: string;
}

const CardAnimated = React.forwardRef<HTMLDivElement, CardAnimatedProps>(
  ({ className, children, id: propId, ...props }, ref) => {
    // Generate ID on client side only to avoid hydration mismatch
    const [id, setId] = React.useState<string>("");
    
    // Set the ID once on client-side only
    React.useEffect(() => {
      if (!id) {
        setId(propId || nanoid());
      }
    }, [propId, id]);
    
    const cardRef = React.useRef<HTMLDivElement>(null);
    const mergedRef = useMergedRef(ref, cardRef);
    
    // Get animation context
    const { registerCard, config } = useLivingCards();
    
    // Use the interaction hook
    const { isInteracting } = useLivingCardInteraction(cardRef, id || "temp-id");
    
    // Register for animations if enabled
    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      
      if (config.enabled && cardRef.current && id) {
        // Add CSS class for client-side styling
        const currentCard = cardRef.current;
        currentCard.classList.add('living-card');
        
        const unregister = registerCard(currentCard);
        return () => {
          unregister();
          if (currentCard) {
            currentCard.classList.remove('living-card');
          }
        };
      }
    }, [config.enabled, registerCard, id]);
    
    return (
      <Card 
        ref={mergedRef}
        className={cn(
          "transition-property-[border-color] duration-1000 hover:border-accent hover:duration-100", 
          {
            // This class is managed by the interaction hook
            "border-accent": isInteracting
          },
          className
        )} 
        data-living-card-id={id}
        tabIndex={props.tabIndex || 0} // Make focusable for keyboard users
        {...props}
      >
        {children}
      </Card>
    );
  }
);

CardAnimated.displayName = "CardAnimated";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  CardAnimated,
}